import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Badge,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { BaseSyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import { ProgramNames } from '@/models/Program';
import { QuestionReviewProps } from '@/models/QuestionReview';
import { UserType, userTypeAsString } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { getApplicationReviewerRoles } from '@/utils/applicationUtil';
import CommentThreadCard from './CommentThreadCard';

function CommentBlock({
  id,
  toggleShowComment,
  chatClicked,
  chatContent,
  addComment,
  resolveComment,
}: {
  id: number;
  toggleShowComment: () => void;
  chatClicked: boolean;
  chatContent: QuestionReviewProps[];
  addComment: (newComment: string) => Promise<boolean>;
  resolveComment: (newCommentResolvedValue: boolean) => void;
}) {
  const { currentContact, userType, usersAll } = useContext(UsersContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { currentApplication } = useContext(CurrentApplicationContext);
  const { translatedText } = useContext(LocaleContext);
  const { setAlert } = useContext(SnackbarContext);
  const theme = useTheme();

  const [remainingCharacters, setRemainingCharacters] = useState(4000);
  const [newComment, setNewComment] = useState('');
  const [showResolveButton, setShowResolveButton] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const commentResolved = chatContent?.every((chat: QuestionReviewProps) => chat.resolved) ?? false;
  const userTimeZone = dayjs.tz.guess();

  const sortedChatContent = useMemo(
    () => [...(chatContent ?? [])].sort((a, b) => dayjs(a.reviewDate).diff(dayjs(b.reviewDate))),
    [chatContent]
  );
  const buttonText = commentResolved
    ? translatedText.common.buttonReactivate
    : translatedText.common.buttonResolve;

  const reviewerAccentColors = useMemo(
    () => [
      theme.palette.ginko.dark,
      theme.palette.taiga.dark,
      theme.palette.sand.dark,
      theme.palette.desert.dark,
      theme.palette.forest.dark,
      theme.palette.ocean.light,
      theme.palette.sky.dark,
    ],
    [theme]
  );

  const reviewerColorById = useMemo(() => {
    const map = new Map<string, string>();
    let paletteIndex = 0;

    sortedChatContent.forEach((chat) => {
      const reviewerKey =
        chat.reviewerId != null
          ? `id:${chat.reviewerId}`
          : `name:${chat.reviewerName ?? 'reviewer-unknown'}`;

      if (!map.has(reviewerKey)) {
        map.set(reviewerKey, reviewerAccentColors[paletteIndex % reviewerAccentColors.length]);
        paletteIndex += 1;
      }
    });

    return map;
  }, [reviewerAccentColors, sortedChatContent]);

  const getReviewerAccentColor = useCallback(
    (chat: QuestionReviewProps) => {
      const reviewerKey =
        chat.reviewerId != null
          ? `id:${chat.reviewerId}`
          : `name:${chat.reviewerName ?? 'reviewer-unknown'}`;

      return reviewerColorById.get(reviewerKey) ?? theme.palette.taiga.main;
    },
    [reviewerColorById, theme]
  );

  const isCurrentUserComment = useCallback(
    (chat: QuestionReviewProps) => {
      if (chat.reviewerId == null || currentContact?.id == null) {
        return false;
      }

      return Number(chat.reviewerId) === Number(currentContact.id);
    },
    [currentContact?.id]
  );

  const getReviewerUserTypeLabel = useCallback(
    (chat: QuestionReviewProps) => {
      const reviewer =
        chat.reviewerId != null && usersAll != null && usersAll.length > 0
          ? usersAll.find(
              (user) =>
                Number(user.contactId) === Number(chat.reviewerId) ||
                Number(user.userContactId) === Number(chat.reviewerId) ||
                Number(user.contact?.id) === Number(chat.reviewerId)
            )
          : null;

      if (reviewer?.userType != null) {
        return userTypeAsString(reviewer.userType);
      }

      switch (chat.reviewStatus) {
        case ApplicationStatus.PendingRegionalReview:
          return userTypeAsString(UserType.RegionalCoordinator);
        case ApplicationStatus.PendingStateReview:
          return userTypeAsString(UserType.StateCoordinator);
        case ApplicationStatus.PendingNationalReview:
          return userTypeAsString(UserType.NationalProgrammeLead);
        case ApplicationStatus.PendingAdfReview:
          return userTypeAsString(UserType.Adf);
        case ApplicationStatus.InRevision:
          return userTypeAsString(UserType.Applicant);
        default:
          return 'Reviewer';
      }
    },
    [usersAll]
  );

  useEffect(() => {
    const textLength = newComment?.length ?? 0;
    setRemainingCharacters(4000 - textLength);
  }, [newComment]);

  useEffect(() => {
    if (!showResolveButton && userType != UserType.Applicant) {
      setShowResolveButton(chatContent != null && chatContent.length > 0);
    }
  }, [chatContent, showResolveButton, userType]);

  function handleResolveClicked(event: BaseSyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    resolveComment(!commentResolved);
  }

  const commentsAllowed = useMemo(() => {
    return (
      currentApplication != null &&
      ![
        ApplicationStatus.ApplicationApproved,
        ApplicationStatus.Closed,
        ApplicationStatus.Submitted,
        ApplicationStatus.Denied,
      ].includes(currentApplication.status)
    );
  }, [currentApplication]);

  const canPostComment = useMemo(() => {
    const currentStatus = currentApplication?.status;
    if (currentStatus == null) {
      return false;
    }

    const { isRegionalReviewer, isStateReviewer } = getApplicationReviewerRoles(
      userType,
      currentStatus,
      selectedProgramId
    );

    const isApplicant =
      userType == UserType.Applicant && currentStatus === ApplicationStatus.InRevision;

    const isNationalProgrammeLeadCommenter =
      userType == UserType.NationalProgrammeLead &&
      currentStatus === ApplicationStatus.PendingNationalReview &&
      selectedProgramId === ProgramNames.TreeCitiesWorld;

    const isAdfCommenter = userType == UserType.Adf;

    return (
      commentsAllowed &&
      (isApplicant ||
        isRegionalReviewer ||
        isStateReviewer ||
        isNationalProgrammeLeadCommenter ||
        isAdfCommenter)
    );
  }, [commentsAllowed, currentApplication?.status, selectedProgramId, userType]);

  const handlePostComment = useCallback(async () => {
    if (newComment.length <= 0) {
      setAlert({
        message: translatedText.common.messageCommentCannotBeEmpty,
        severity: 'warning',
      });

      return;
    }

    if (remainingCharacters < 0) {
      setAlert({
        message: translatedText.common.messageCommentTooLong,
        severity: 'warning',
      });

      return;
    }

    setPostingComment(true);
    const success = await addComment(newComment);
    if (success) {
      setNewComment('');
      setAlert({
        message: translatedText.common.messageCommentAddedSuccessfully,
        severity: 'success',
      });
    } else {
      setAlert({
        message: translatedText.common.messageCommentAddFailed,
        severity: 'error',
      });
    }
    setPostingComment(false);
  }, [
    addComment,
    newComment,
    remainingCharacters,
    setAlert,
    translatedText.common.messageCommentAddFailed,
    translatedText.common.messageCommentAddedSuccessfully,
    translatedText.common.messageCommentCannotBeEmpty,
    translatedText.common.messageCommentTooLong,
  ]);

  const hasComments = (chatContent?.length ?? 0) > 0;
  const badgeContent =
    commentResolved && hasComments ? (
      <CheckIcon sx={{ fontSize: '1rem', fontWeight: 700 }} />
    ) : hasComments ? (
      <Box
        component='span'
        sx={{
          width: '100%',
          height: '100%',
          display: 'grid',
          placeItems: 'center',
          lineHeight: 1,
          textAlign: 'center',
          transform: 'translateY(-0.75px)',
        }}
      >
        {chatContent.length}
      </Box>
    ) : (
      <AddIcon sx={{ fontSize: '1rem', fontWeight: 700 }} />
    );
  const badgeColor =
    commentResolved && hasComments
      ? theme.palette.success.main
      : hasComments
        ? theme.palette.error.main
        : theme.palette.sand.main;

  const badgeBorder = hasComments ? null : `2px solid ${theme.palette.desert.main}`;

  const chatBubbleIconColor = theme.palette.taiga.main;

  const hideBadge = chatClicked || (!commentResolved && !hasComments);
  const commentsResolved =
    sortedChatContent.filter((chat: QuestionReviewProps) => chat.resolved).length > 0;
  return (
    <Box display='flex' alignSelf='center'>
      <Box height='auto'>
        <IconButton
          id={id.toString()}
          className={id.toString()}
          sx={{
            marginLeft: '10px',
            alignItems: 'start',
            padding: '0px 8px',
            display:
              !commentsAllowed && (chatContent == null || chatContent.length == 0)
                ? 'none'
                : 'flex',
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleShowComment();
          }}
        >
          <Badge
            badgeContent={badgeContent}
            invisible={hideBadge}
            sx={{
              padding: '0px',
              '& .MuiBadge-badge': {
                border: badgeBorder,
                backgroundColor: badgeColor,
                color: theme.palette.getContrastText(badgeColor),
                minWidth: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                lineHeight: 1,
                fontSize: '0.7rem',
                fontWeight: 600,
              },
              '& .MuiBadge-colorInfo': {
                backgroundColor: theme.palette.text.secondary,
              },
              '& .MuiBadge-colorSuccess': {
                backgroundColor: theme.palette.success.main,
                color: theme.palette.common.white,
              },
            }}
          >
            <ChatBubbleIcon sx={{ color: chatBubbleIconColor }} id={id.toString()} />
          </Badge>
        </IconButton>
      </Box>

      {chatClicked && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.sandShade3.main}`,
            backgroundColor: theme.palette.sandTint.main,
          }}
        >
          {commentsResolved && (
            <Box margin='10px'>
              <Alert severity='success' sx={{ width: '100%' }}>
                {translatedText.common.commentResolved}
              </Alert>
            </Box>
          )}

          {chatContent != null && chatContent.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: { xs: 'min(92vw, 360px)', sm: '360px' },
                minHeight: '200px',
                overflow: 'hidden',
                padding: '15px',
              }}
            >
              <Box width='100%' textAlign='left'>
                <Box
                  sx={{
                    maxHeight: sortedChatContent.length > 0 ? '450px' : '320px',
                    overflowY: 'auto',
                    paddingRight: '2px',
                  }}
                >
                  {sortedChatContent.map((chat: QuestionReviewProps) => {
                    const accentColor = getReviewerAccentColor(chat);
                    const currentUserComment = isCurrentUserComment(chat);
                    const reviewerUserTypeLabel = getReviewerUserTypeLabel(chat);
                    return (
                      <CommentThreadCard
                        key={`chat-${chat.reviewerName}-${chat.reviewDate}`}
                        chat={chat}
                        accentColor={accentColor}
                        isCurrentUserComment={currentUserComment}
                        reviewerUserTypeLabel={reviewerUserTypeLabel}
                        userTimeZone={userTimeZone}
                        resolved={chat.resolved ?? false}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {canPostComment && !commentsResolved && (
            <Box
              sx={{
                borderTop:
                  chatContent != null && chatContent.length > 0
                    ? `1px solid ${theme.palette.divider}`
                    : 'none',
                padding: '15px',
                width: { xs: 'min(92vw, 360px)', sm: '360px' },
              }}
            >
              <TextField
                sx={{
                  width: '100%',
                  maxWidth: '360px',
                  minHeight: '100px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                }}
                disabled={postingComment}
                minRows={3}
                multiline
                id={id.toString()}
                placeholder={translatedText.common.placeholderPostComment}
                value={newComment}
                onChange={(e: BaseSyntheticEvent) => {
                  const updatedValue = (e.target as HTMLInputElement).value;
                  setNewComment(updatedValue);
                }}
              />

              <Box
                style={{
                  position: 'relative',
                  top: '-5px',
                  marginTop: '5px',
                  marginBottom: '15px',
                }}
              >
                <Box
                  style={{
                    width: '310px',
                    position: 'absolute',
                    right: '0px',
                    textAlign: 'right',
                  }}
                >
                  <Typography
                    variant='subtitle3'
                    color={
                      remainingCharacters == 0 ? theme.palette.error.main : theme.palette.taiga.main
                    }
                  >
                    {translatedText.common.characterCountRemaining(remainingCharacters, 4000)}
                  </Typography>
                </Box>
              </Box>

              <Box
                display='flex'
                justifyContent={showResolveButton ? 'space-between' : 'flex-end'}
                width='100%'
                sx={{ marginTop: '32px' }}
              >
                {showResolveButton && (
                  <Button
                    sx={{
                      marginTop: '5px',
                    }}
                    id={id.toString()}
                    size='small'
                    variant='secondary'
                    disabled={postingComment}
                    onClick={(e: BaseSyntheticEvent) => {
                      handleResolveClicked(e);
                    }}
                  >
                    {buttonText}
                  </Button>
                )}

                <Button
                  sx={{
                    marginTop: '5px',
                  }}
                  id={id.toString()}
                  size='small'
                  variant='primary'
                  disabled={postingComment}
                  onClick={handlePostComment}
                >
                  {translatedText.common.buttonPost}
                </Button>
              </Box>
            </Box>
          )}

          {commentsResolved && (
            <Box width='100%' justifyContent='center' display='flex' borderTop='1px solid #e0e0e0'>
              <Button
                sx={{
                  margin: 1.5,
                }}
                id={id.toString()}
                size='small'
                variant='secondary'
                disabled={postingComment}
                onClick={(e: BaseSyntheticEvent) => {
                  handleResolveClicked(e);
                }}
              >
                {buttonText}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default CommentBlock;
